import { apiClient } from '../../../api/client'
import type { EmployeePick } from '../../minutes/types'

/** Asistan uçlarının tabanı — kişisel kapsam (yetki istemez, oturum kimliğine daraltılır). */
const BASE = '/me/assistant'

/** Aday arama ucu — korumalı grup; `ASSISTANT_MANAGE` ister. */
const CANDIDATES = '/management/assistant-candidates'

/**
 * Sayfalama gerekmeyen bir arama için sayfa boyutu. Uç anayasa gereği yine sayfalı döner; kullanıcı
 * yazdıkça daraltılan bir ad aramasında ilk sayfa yeterlidir.
 */
const PAGE_SIZE = 20

/** Aktif asistan ataması (`contracts/assistant-api.md`). */
export interface Assistant {
  employeeId: string
  fullName: string
  jobTitle: string | null
  businessMail: string | null
  authorityId: number
  authorityName: string | null
  /** `YYYY-MM-DD` — yalnız gün; saat ayrıntısının ekranda karşılığı yok. */
  assignmentDate: string | null
}

/**
 * Asistan ekranının açılış durumu.
 *
 * `canDefine` iki koşulu birden taşır (kadro uygunluğu **ve** aday arama yetkisi) — sunucu bunları
 * ayrı ayrı sızdırmaz, çünkü arayüzün ikisini ayrı değerlendirmesi için bir sebep yok ve ayrı
 * gelselerdi menü ile arama farklı kararlar verebilirdi.
 */
export interface AssistantState {
  canDefine: boolean
  /** Aktif atama yoksa `null` — alan her zaman gelir. */
  assistant: Assistant | null
  /**
   * Yetki seçenekleri, katalogdan ve istek dilinde. Ayrı bir uçtan çekilmez: ekran zaten açılışta bu
   * ucu çağırıyor ve seçenekler olmadan form çizilemez. Adlar sunucudan geldiği için üçüncü bir
   * seviye eklenirse arayüz kendiliğinden gösterir.
   */
  authorities: AuthorityOption[]
}

/** Yetki seviyesi seçeneği — ortak lookup biçimi. */
export interface AuthorityOption {
  id: number
  name: string
}

interface PagedCandidates {
  content: EmployeePick[]
}

/** TanStack Query anahtarları — geçersiz kılma noktalarının aynı anahtarı kullanması için tek yerde. */
export const assistantKeys = {
  /**
   * Durum önbelleği **kimliğe bağlıdır** — anahtara seçili personanın `userId`'si girer.
   *
   * Gerekçe ölçüldü (20.10.2026, kullanıcı bildirimi): bu sorgu `staleTime: Infinity` taşıyor ve
   * Topbar'da, yani **kabukta** yaşıyor. `AppLayout` yalnız İÇERİK ağacını personayla anahtarlıyor;
   * kabuk sökülmediği için persona değişince sorgu yeniden mount olmuyor ve
   * `invalidateQueries({ refetchType: 'none' })` onu yalnız bayat işaretliyor. Sonuç: persona
   * değiştikten sonra menü ÖNCEKİ kimliğin `canDefine` değerini gösteriyor, "Asistan Tanımla"
   * açılıyor ve kişi aramada 403 alınıyordu — tasarımın kaçınmak için kurulduğu çıkmazın ta kendisi.
   *
   * Anahtarı kimliğe bağlamak bunu **yapısal olarak** çözer: persona değişince anahtar değişir,
   * TanStack o kimliğe ait veri olmadığı için taze çeker. `finishSwitch`'e tek tek sorgu eklemeyi
   * (ve yeni sorgularda hatırlamayı) gerektirmez.
   */
  state: (userId?: number | null) => ['assistant-state', userId ?? null] as const,
  candidates: (q: string) => ['assistant-candidates', q] as const,
}

/** Uygunluk + varsa aktif atama. Tek çağrı; `/me` sözleşmesi bu yüzden hiç değişmedi. */
export async function getAssistantState(): Promise<AssistantState> {
  const { data } = await apiClient.get<AssistantState>(BASE)
  return data
}

/**
 * Asistan adaylarını İK'da arar.
 *
 * **Kıdem süzgeci yoktur** — tipik yönetici asistanı büro şefi düzeyindedir ve ortak kişi seçici ucu
 * onu hiç döndürmez. Bu yüzden ayrı bir uç kullanılır (gerekçe `contracts/assistant-api.md`).
 */
export async function searchAssistantCandidates(q: string): Promise<EmployeePick[]> {
  const { data } = await apiClient.get<PagedCandidates>(CANDIDATES, {
    params: { q, page: 0, size: PAGE_SIZE },
  })
  return data.content
}

/** Asistanı atar ya da değiştirir (tekil alt kaynak → upsert). */
export async function upsertAssistant(employeeId: string, authorityId: number): Promise<Assistant> {
  const { data } = await apiClient.put<Assistant>(BASE, { employeeId, authorityId })
  return data
}

/** Aktif asistanı kaldırır (soft-delete; kayıt tarihçe olarak kalır). */
export async function removeAssistant(): Promise<void> {
  await apiClient.delete(BASE)
}
